import { ApplicationUuid, OPTIMIZATION_TYPE, Run, RunDetails, Task, TaskStatus, WayPoint } from '@bringg/types';
import { getActionableWaypoint } from '@bringg-frontend/global-stores';
import { optimizationTypeProvider, RouteGroup } from '@bringg-frontend/eta-calculator';
import moment from 'moment';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';

import RootStore from 'bringg-web/stores/root-store';

export type OriginalTaskData = Pick<Task, 'id' | 'priority' | 'status'> & Pick<WayPoint, 'lat' | 'lng'>;

export const didTaskDataChanged = (task: OriginalTaskData) => {
	const updatedTask = getRootEnv().dashboardSdk.sdk.v2.tasks.get(task.id);

	if (!updatedTask) {
		return true;
	}

	const updatedWaypoint = getActionableWaypoint(updatedTask);

	return (
		updatedTask.priority !== task.priority ||
		updatedWaypoint.lat !== task.lat ||
		updatedWaypoint.lng !== task.lng ||
		(updatedTask.status === TaskStatus.Cancelled && task.status !== TaskStatus.Cancelled)
	);
};

export const mapTaskOriginData = (task: Task): OriginalTaskData => {
	const waypoint = getActionableWaypoint(task);
	const { id, priority, status } = task;
	const { lat, lng } = waypoint;

	return {
		id,
		priority,
		status,
		lat,
		lng
	};
};

const sortTasksByEtaAndPriority = (taskA, taskB) => {
	const wpAEta = moment(getActionableWaypoint(taskA).eta);
	const wpBEta = moment(getActionableWaypoint(taskB).eta);

	if (wpAEta > wpBEta || (!wpAEta && wpBEta)) {
		return 1;
	} else if (wpAEta < wpBEta || (!wpBEta && wpAEta)) {
		return -1;
	}

	// Else sort by priority
	if (taskA.priority < taskB.priority) {
		return -1;
	} else if (taskA.priority > taskB.priority) {
		return 1;
	}
	return 0;
};

export const getRouteData = (route: RunDetails, tasks: Task[]): RouteGroup => {
	const runOrderedTasks = tasks.sort(sortTasksByEtaAndPriority);

	const { id, vehicle_id, started_at, scheduled_start_time, ended_at, scheduled_end_time, user_id } = route;

	const plannedRouteData = {
		runId: id,
		driverId: user_id,
		vehicleId: vehicle_id,
		routeIndex: undefined,
		runOrderedTasks,
		runStartTime: Date.parse(started_at || scheduled_start_time) / 1000,
		runEndTime: Date.parse(ended_at || scheduled_end_time) / 1000
	};

	return {
		routes: [plannedRouteData],
		minStartTimeMs: undefined,
		totalRunIds: [id]
	};
};

export const getRouteTasks = (route: Run): Task[] => {
	if (!route) {
		return [];
	}
	return route.task_ids
		.map(id => getRootEnv().dashboardSdk.sdk.v2.tasks.get(id))
		.filter(task => task && ![TaskStatus.Cancelled, TaskStatus.Done, TaskStatus.Rejected].includes(task.status));
};

export const getOptimizationTypeByRouteTeam = (teamId: number): OPTIMIZATION_TYPE => {
	const team = getRoot<RootStore>().data.teamsStore.get(teamId);
	const configuration = getRoot<RootStore>().data.merchantConfigurationsStore.findApplicationConfiguration(
		ApplicationUuid.RouteOptimizer2
	);
	return optimizationTypeProvider.get(team, configuration);
};
